import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/api';

const AdminReconciliation = () => {
  const [summaryData, setSummaryData] = useState([]);
  const [bookingsData, setBookingsData] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');

  // Filters
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [selectedPlatform, setSelectedPlatform] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [discountFilter, setDiscountFilter] = useState('all'); // 'all', 'discounted', 'no_discount'

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (token) {
      fetchSummary();
    }
  }, [token, startDate, endDate]);

  useEffect(() => {
    if (token && activeTab === 'bookings') {
      fetchBookings();
    } else if (token && activeTab === 'daily') {
      fetchDaily();
    }
  }, [token, activeTab, startDate, endDate, selectedPlatform, selectedStatus, discountFilter]);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);

      const response = await fetch(`${API_URL}/reports/reconciliation/by-platform?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setSummaryData(data.data || []);
        setSummary(data.summary || {});
        setError('');
      } else {
        setError(data.message || 'Failed to load reconciliation data');
      }
    } catch (err) {
      console.error('Error fetching reconciliation:', err);
      setError('Failed to load reconciliation data');
    } finally {
      setLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedPlatform !== 'all') params.append('platform', selectedPlatform);
      if (selectedStatus !== 'all') params.append('status', selectedStatus);

      const response = await fetch(`${API_URL}/reports/reconciliation/bookings?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        let results = data.data || [];
        // Apply discount filter client-side
        if (discountFilter === 'discounted') {
          results = results.filter(b => b.discounts > 0 || (b.notes && b.notes.includes('[Discount:')));
        } else if (discountFilter === 'no_discount') {
          results = results.filter(b => !b.discounts && !(b.notes && b.notes.includes('[Discount:')));
        }
        setBookingsData(results);
        setError('');
      } else {
        setError(data.message || 'Failed to load bookings');
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDaily = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (selectedPlatform !== 'all') params.append('platform', selectedPlatform);

      const response = await fetch(`${API_URL}/reports/reconciliation/daily?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        setDailyData(data.data || []);
        setError('');
      } else {
        setError(data.message || 'Failed to load daily data');
      }
    } catch (err) {
      console.error('Error fetching daily:', err);
      setError('Failed to load daily data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '-';
    return timeStr.substring(0, 5);
  };

  const getPlatformColor = (platform) => {
    const colors = {
      playo: '#4F46E5',
      hudle: '#10B981',
      direct: '#F59E0B'
    };
    return colors[platform?.toLowerCase()] || '#6B7280';
  };

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'admin-badge admin-badge-success',
      cancelled: 'admin-badge admin-badge-danger',
      pending: 'admin-badge admin-badge-warning'
    };
    return styles[status] || 'admin-badge';
  };

  // Helper to check if booking has discount
  const hasDiscount = (booking) => {
    return booking.discounts > 0 || (booking.notes && booking.notes.includes('[Discount:'));
  };

  // Calculate discount summary from bookings
  const getDiscountSummary = () => {
    const discountedBookings = bookingsData.filter(hasDiscount);
    const totalDiscountAmount = discountedBookings.reduce((sum, b) => {
      // Extract discount from notes if available
      const match = b.notes?.match(/Discount: ₹(\d+)/);
      return sum + (match ? parseInt(match[1]) : (b.discounts || 0));
    }, 0);
    return {
      count: discountedBookings.length,
      totalAmount: totalDiscountAmount
    };
  };

  const exportToCSV = () => {
    let csvContent = '';
    let filename = '';

    if (activeTab === 'summary') {
      // Platform Summary CSV
      filename = `reconciliation_summary_${startDate}_to_${endDate}.csv`;
      csvContent = 'Platform,Total Bookings,Confirmed,Cancelled,Gross Revenue,Commission,Net Revenue,Commission %\n';
      summaryData.forEach(row => {
        csvContent += `${row.platform},${row.totalBookings},${row.confirmedBookings},${row.cancelledBookings},${row.grossRevenue},${row.totalCommission},${row.netRevenue},${row.configuredCommissionRate || ''}\n`;
      });
      // Add totals row
      csvContent += `\nTOTAL,${summary.total_bookings || 0},${summary.confirmed_bookings || 0},,${summary.gross_revenue || 0},${summary.total_commission || 0},${summary.net_revenue || 0},\n`;
    } else if (activeTab === 'bookings') {
      // Booking Details CSV
      filename = `reconciliation_bookings_${startDate}_to_${endDate}.csv`;
      csvContent = 'Date,Start Time,End Time,Court,Sport,Customer Name,Customer Phone,Platform,Status,Amount,Commission,Net Revenue\n';
      bookingsData.forEach(booking => {
        const date = booking.bookingDate ? new Date(booking.bookingDate).toLocaleDateString('en-IN') : '';
        csvContent += `${date},${booking.startTime || ''},${booking.endTime || ''},"${booking.courtName || ''}","${booking.sportName || ''}","${booking.customerName || ''}","${booking.customerPhone || ''}",${booking.platform},${booking.status},${booking.totalAmount},${booking.platformCommission},${booking.netRevenue}\n`;
      });
    } else if (activeTab === 'daily') {
      // Daily Breakdown CSV
      filename = `reconciliation_daily_${startDate}_to_${endDate}.csv`;
      csvContent = 'Date,Platform,Bookings,Gross Revenue,Commission,Net Revenue\n';
      dailyData.forEach(row => {
        const date = row.date ? new Date(row.date).toLocaleDateString('en-IN') : '';
        csvContent += `${date},${row.platform},${row.bookingCount},${row.grossRevenue},${row.commission},${row.netRevenue}\n`;
      });
    }

    // Download the CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  if (!token) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      {/* Header with Filters */}
      <div className="admin-card" style={{ marginBottom: '16px' }}>
        <div className="admin-card-body">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', alignItems: 'flex-end' }}>
            <div className="admin-field">
              <label className="admin-label">Start Date</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="admin-input"
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">End Date</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="admin-input"
              />
            </div>
            <div className="admin-field">
              <label className="admin-label">Platform</label>
              <select
                value={selectedPlatform}
                onChange={(e) => setSelectedPlatform(e.target.value)}
                className="admin-select"
              >
                <option value="all">All Platforms</option>
                <option value="playo">Playo</option>
                <option value="hudle">Hudle</option>
                <option value="direct">Direct</option>
              </select>
            </div>
            {activeTab === 'bookings' && (
              <>
                <div className="admin-field">
                  <label className="admin-label">Status</label>
                  <select
                    value={selectedStatus}
                    onChange={(e) => setSelectedStatus(e.target.value)}
                    className="admin-select"
                  >
                    <option value="all">All Status</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="pending">Pending</option>
                  </select>
                </div>
                <div className="admin-field">
                  <label className="admin-label">Discount</label>
                  <select
                    value={discountFilter}
                    onChange={(e) => setDiscountFilter(e.target.value)}
                    className="admin-select"
                  >
                    <option value="all">All Bookings</option>
                    <option value="discounted">Discounted Only</option>
                    <option value="no_discount">No Discount</option>
                  </select>
                </div>
              </>
            )}
            <button
              onClick={() => {
                fetchSummary();
                if (activeTab === 'bookings') fetchBookings();
                if (activeTab === 'daily') fetchDaily();
              }}
              className="admin-btn admin-btn-primary"
            >
              Refresh
            </button>
            <button
              onClick={exportToCSV}
              disabled={loading || (activeTab === 'summary' && summaryData.length === 0) || (activeTab === 'bookings' && bookingsData.length === 0) || (activeTab === 'daily' && dailyData.length === 0)}
              className="admin-btn admin-btn-secondary"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {error && <div className="admin-alert error">{error}</div>}

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '16px' }}>
        <div className="admin-card">
          <div className="admin-card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#4F46E5' }}>
              {summary.total_bookings || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Total Bookings</div>
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#10B981' }}>
              {summary.confirmed_bookings || 0}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Confirmed</div>
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#059669' }}>
              {formatCurrency(summary.gross_revenue || 0)}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Gross Revenue</div>
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#DC2626' }}>
              {formatCurrency(summary.total_commission || 0)}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Total Commission</div>
          </div>
        </div>
        <div className="admin-card">
          <div className="admin-card-body" style={{ textAlign: 'center', padding: '16px' }}>
            <div style={{ fontSize: '28px', fontWeight: 700, color: '#7C3AED' }}>
              {formatCurrency(summary.net_revenue || 0)}
            </div>
            <div style={{ fontSize: '12px', color: '#6B7280' }}>Net Revenue</div>
          </div>
        </div>
        {activeTab === 'bookings' && bookingsData.length > 0 && (
          <div className="admin-card" style={{ backgroundColor: '#fef3c7', border: '1px solid #fde047' }}>
            <div className="admin-card-body" style={{ textAlign: 'center', padding: '16px' }}>
              <div style={{ fontSize: '28px', fontWeight: 700, color: '#d97706' }}>
                {getDiscountSummary().count}
              </div>
              <div style={{ fontSize: '12px', color: '#92400e' }}>Discounted Bookings</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          onClick={() => setActiveTab('summary')}
          className={`admin-btn ${activeTab === 'summary' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
        >
          Platform Summary
        </button>
        <button
          onClick={() => setActiveTab('bookings')}
          className={`admin-btn ${activeTab === 'bookings' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
        >
          Booking Details
        </button>
        <button
          onClick={() => setActiveTab('daily')}
          className={`admin-btn ${activeTab === 'daily' ? 'admin-btn-primary' : 'admin-btn-secondary'}`}
        >
          Daily Breakdown
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'summary' && (
        <div className="admin-card">
          <div className="admin-card-header">Platform-wise Reconciliation</div>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : summaryData.length === 0 ? (
            <div className="admin-empty">
              <p>No bookings found for the selected period</p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Platform</th>
                    <th>Total Bookings</th>
                    <th>Confirmed</th>
                    <th>Cancelled</th>
                    <th>Gross Revenue</th>
                    <th>Commission</th>
                    <th>Net Revenue</th>
                    <th>Commission %</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryData.map((row, index) => (
                    <tr key={index}>
                      <td>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          backgroundColor: getPlatformColor(row.platform) + '20',
                          color: getPlatformColor(row.platform),
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {row.platform}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{row.totalBookings}</td>
                      <td style={{ color: '#10B981' }}>{row.confirmedBookings}</td>
                      <td style={{ color: '#DC2626' }}>{row.cancelledBookings}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(row.grossRevenue)}</td>
                      <td style={{ color: '#DC2626' }}>{formatCurrency(row.totalCommission)}</td>
                      <td style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(row.netRevenue)}</td>
                      <td>
                        {row.configuredCommissionRate !== null
                          ? `${row.configuredCommissionRate}%`
                          : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'bookings' && (
        <div className="admin-card">
          <div className="admin-card-header">Booking Details ({bookingsData.length})</div>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : bookingsData.length === 0 ? (
            <div className="admin-empty">
              <p>No bookings found for the selected filters</p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Time</th>
                    <th>Court</th>
                    <th>Customer</th>
                    <th>Platform</th>
                    <th>Status</th>
                    <th>Amount</th>
                    <th>Commission</th>
                    <th>Net</th>
                  </tr>
                </thead>
                <tbody>
                  {bookingsData.map((booking) => (
                    <tr key={booking.id} style={hasDiscount(booking) ? { backgroundColor: '#fef3c7' } : {}}>
                      <td>{formatDate(booking.bookingDate)}</td>
                      <td>{formatTime(booking.startTime)} - {formatTime(booking.endTime)}</td>
                      <td>
                        <div style={{ fontWeight: 600 }}>{booking.courtName}</div>
                        <div style={{ fontSize: '11px', color: '#6B7280' }}>{booking.sportName}</div>
                      </td>
                      <td>
                        <div>{booking.customerName}</div>
                        <div style={{ fontSize: '11px', color: '#6B7280' }}>{booking.customerPhone}</div>
                      </td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '8px',
                          backgroundColor: getPlatformColor(booking.platform) + '20',
                          color: getPlatformColor(booking.platform),
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {booking.platform}
                        </span>
                      </td>
                      <td>
                        <span className={getStatusBadge(booking.status)}>
                          {booking.status}
                        </span>
                      </td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(booking.totalAmount)}</td>
                      <td style={{ color: '#DC2626' }}>{formatCurrency(booking.platformCommission)}</td>
                      <td style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(booking.netRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'daily' && (
        <div className="admin-card">
          <div className="admin-card-header">Daily Breakdown</div>
          {loading ? (
            <div className="admin-loading">
              <div className="admin-spinner"></div>
            </div>
          ) : dailyData.length === 0 ? (
            <div className="admin-empty">
              <p>No data found for the selected period</p>
            </div>
          ) : (
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Platform</th>
                    <th>Bookings</th>
                    <th>Gross Revenue</th>
                    <th>Commission</th>
                    <th>Net Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {dailyData.map((row, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: 600 }}>{formatDate(row.date)}</td>
                      <td>
                        <span style={{
                          padding: '2px 8px',
                          borderRadius: '8px',
                          backgroundColor: getPlatformColor(row.platform) + '20',
                          color: getPlatformColor(row.platform),
                          fontSize: '11px',
                          fontWeight: 600,
                          textTransform: 'capitalize'
                        }}>
                          {row.platform}
                        </span>
                      </td>
                      <td>{row.bookingCount}</td>
                      <td style={{ fontWeight: 600 }}>{formatCurrency(row.grossRevenue)}</td>
                      <td style={{ color: '#DC2626' }}>{formatCurrency(row.commission)}</td>
                      <td style={{ fontWeight: 600, color: '#059669' }}>{formatCurrency(row.netRevenue)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminReconciliation;
